/**
 * QR Code Generator with Embedded Images
 * 
 * Based on:
 * - Original awesome-qr.js by Makito (SumiMakito)
 *   GitHub: https://github.com/SumiMakito/Awesome-qr.js
 * 
 * - react-awesome-qr wrapper
 *   NPM: https://www.npmjs.com/package/react-awesome-qr
 * 
 * - Initial CodeSandbox template
 *   Created with CodeSandbox
 * 
 * Enhanced with custom UI and controls
 */

import { useState, useRef } from "react";
import ReactQr from "react-awesome-qr";
import "./styles.css";
import defaultBgImg from "./fundo.jpg";

// Importar componentes modulares
import QRTypeSelector from "./components/QRTypeSelector";
import URLQRForm from "./components/URLQRForm";
import TextQRForm from "./components/TextQRForm";
import VCardQRForm from "./components/VCardQRForm";
import WiFiQRForm from "./components/WiFiQRForm";
import QRPreview from "./components/QRPreview";
import { generateQRText, validateQRData } from "./utils/qrGenerators";

export default function App() {
  // Estados para tipo de QR Code e dados
  const [qrType, setQrType] = useState("url");
  const [qrText, setQrText] = useState("https://qr.capo.bz/");
  const [vcardData, setVcardData] = useState({
    name: '',
    organization: '',
    title: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    note: ''
  });
  const [wifiData, setWifiData] = useState({
    ssid: '',
    password: '',
    security: 'WPA',
    hidden: false
  });

  // Estados para configurações visuais do QR Code
  const [qrSize, setQrSize] = useState(500);
  const [dotScale, setDotScale] = useState(0.5);
  const [correctLevel, setCorrectLevel] = useState(3);
  const [logoScale, setLogoScale] = useState(0.3);
  const [colorDark, setColorDark] = useState("#000000");
  const [colorLight, setColorLight] = useState("#ffffff");
  const [maskTiming, setMaskTiming] = useState(false);
  
  // Estados para imagens
  const [bgImage, setBgImage] = useState(defaultBgImg);
  const [logoImage, setLogoImage] = useState(null);
  
  // Refs para inputs de arquivo
  const bgInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Handler para upload de imagem de fundo
  const handleBgImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBgImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handler para upload de logo
  const handleLogoImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handler para remover logo
  const removeLogo = () => {
    setLogoImage(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  // Handler para resetar imagem de fundo
  const resetBgImage = () => {
    setBgImage(defaultBgImg);
    if (bgInputRef.current) {
      bgInputRef.current.value = "";
    }
  };

  // Handler para mudança de tipo de QR Code
  const handleQrTypeChange = (newType) => {
    setQrType(newType);
    
    // Reset dos dados específicos quando mudar de tipo
    if (newType === 'url') {
      setQrText("https://qr.capo.bz/");
    } else if (newType === 'text') {
      setQrText("Digite seu texto aqui...");
    } else if (newType === 'vcard') {
      // Manter dados do vCard se já existirem
    } else if (newType === 'wifi') {
      // Manter dados do WiFi se já existirem
    }
  };

  // Função para obter dados atuais baseado no tipo
  const getCurrentQRData = () => {
    switch (qrType) {
      case 'url':
      case 'text':
        return qrText;
      case 'vcard':
        return vcardData;
      case 'wifi':
        return wifiData;
      default:
        return qrText;
    }
  };

  // Função para obter texto final do QR Code
  const getFinalQRText = () => {
    const currentData = getCurrentQRData();
    return generateQRText(qrType, currentData);
  };


  // Função para validar dados atuais
  const getCurrentValidation = () => {
    const currentData = getCurrentQRData();
    return validateQRData(qrType, currentData);
  };

  // Opções de components (quando usuário optar por disfarçar padrões estruturais)
  const componentsOpts = maskTiming
    ? {
        data: { scale: dotScale },
        timing: { scale: dotScale, protectors: false },
        alignment: { scale: dotScale, protectors: false },
        cornerAlignment: { scale: dotScale, protectors: true }
      }
    : undefined;

  return (
    <div className="app-container">
      <header className="header">
        <h1>🎨 Gerador de QR Code Personalizado</h1>
        <p>Crie QR Codes únicos com imagens incorporadas</p>
      </header>

      <div className="main-content">
        {/* Painel de Controles */}
        <div className="controls-panel">
          <section className="control-section">
            <h2>🎯 Tipo de QR Code</h2>
            <QRTypeSelector 
              qrType={qrType} 
              onTypeChange={handleQrTypeChange} 
            />
          </section>

          <section className="control-section">
            <h2>📝 Conteúdo do QR Code</h2>
            
            {/* Formulário dinâmico baseado no tipo selecionado */}
            {qrType === 'url' && (
              <URLQRForm 
                qrText={qrText} 
                onTextChange={setQrText} 
              />
            )}
            
            {qrType === 'text' && (
              <TextQRForm 
                qrText={qrText} 
                onTextChange={setQrText} 
              />
            )}
            
            {qrType === 'vcard' && (
              <VCardQRForm 
                vcardData={vcardData} 
                onDataChange={setVcardData} 
              />
            )}
            
            {qrType === 'wifi' && (
              <WiFiQRForm 
                wifiData={wifiData} 
                onDataChange={setWifiData} 
              />
            )}

            {/* Preview do conteúdo que será gerado */}
            <QRPreview 
              qrType={qrType} 
              qrData={getCurrentQRData()} 
              showPreview={true}
            />
          </section>

          <section className="control-section">
            <h2>🖼️ Imagens</h2>
            
            <div className="form-group">
              <label>Imagem de Fundo</label>
              <div className="upload-controls">
                <input
                  ref={bgInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBgImageUpload}
                  className="file-input"
                  id="bgImage"
                />
                <label htmlFor="bgImage" className="file-label">
                  Escolher Imagem
                </label>
                <button onClick={resetBgImage} className="btn-secondary">
                  Resetar
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Logo (opcional)</label>
              <div className="upload-controls">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoImageUpload}
                  className="file-input"
                  id="logoImage"
                />
                <label htmlFor="logoImage" className="file-label">
                  Escolher Logo
                </label>
                {logoImage && (
                  <button onClick={removeLogo} className="btn-secondary">
                    Remover
                  </button>
                )}
              </div>
            </div>
          </section>

          <section className="control-section">
            <h2>⚙️ Configurações</h2>
            
            <div className="form-group">
              <label htmlFor="qrSize">
                Tamanho: {qrSize}px
              </label>
              <input
                id="qrSize"
                type="range"
                min="200"
                max="1000"
                step="50"
                value={qrSize}
                onChange={(e) => setQrSize(Number(e.target.value))}
                className="slider"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dotScale">
                Escala dos Pontos: {dotScale.toFixed(2)}
              </label>
              <input
                id="dotScale"
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={dotScale}
                onChange={(e) => setDotScale(Number(e.target.value))}
                className="slider"
              />
              <small>Valores menores mostram mais a imagem de fundo</small>
            </div>

            {logoImage && (
              <div className="form-group">
                <label htmlFor="logoScale">
                  Tamanho do Logo: {logoScale.toFixed(2)}
                </label>
                <input
                  id="logoScale"
                  type="range"
                  min="0.1"
                  max="0.5"
                  step="0.05"
                  value={logoScale}
                  onChange={(e) => setLogoScale(Number(e.target.value))}
                  className="slider"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="correctLevel">Nível de Correção de Erro</label>
              <select
                id="correctLevel"
                value={correctLevel}
                onChange={(e) => setCorrectLevel(Number(e.target.value))}
                className="select-input"
              >
                <option value={0}>L (Baixo - ~7%)</option>
                <option value={1}>M (Médio - ~15%)</option>
                <option value={2}>Q (Alto - ~25%)</option>
                <option value={3}>H (Muito Alto - ~30%)</option>
              </select>
              <small>Níveis altos permitem mais personalização</small>
            </div>

            <div className="form-group">
              <label htmlFor="maskTiming">Disfarçar linhas de sincronização (timing)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <input
                  id="maskTiming"
                  type="checkbox"
                  checked={maskTiming}
                  onChange={(e) => setMaskTiming(e.target.checked)}
                />
                <small>Reduz o destaque das linhas que conectam os detectores de posição</small>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="colorDark">Cor Escura</label>
              <div className="color-input-group">
                <input
                  id="colorDark"
                  type="color"
                  value={colorDark}
                  onChange={(e) => setColorDark(e.target.value)}
                  className="color-input"
                />
                <input
                  type="text"
                  value={colorDark}
                  onChange={(e) => setColorDark(e.target.value)}
                  className="color-text"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="colorLight">Cor Clara</label>
              <div className="color-input-group">
                <input
                  id="colorLight"
                  type="color"
                  value={colorLight}
                  onChange={(e) => setColorLight(e.target.value)}
                  className="color-input"
                />
                <input
                  type="text"
                  value={colorLight}
                  onChange={(e) => setColorLight(e.target.value)}
                  className="color-text"
                />
              </div>
            </div>
          </section>
        </div>

        {/* Painel de Preview */}
        <div className="preview-panel">
          <h2>👁️ Preview</h2>
          <div className="qr-container">
            <div className="qr-wrapper">
              <div className="qr-positioner">
                <ReactQr
                  text={getFinalQRText()}
                  size={qrSize}
                  dotScale={dotScale}
                  correctLevel={correctLevel}
                  bgSrc={bgImage}
                  logoSrc={logoImage}
                  logoScale={logoScale}
                  colorDark={colorDark}
                  colorLight={colorLight}
                  whiteMargin={!logoImage}        // SEM bordas quando há logo
                  autoColor={false}
                  binarize={false}
                  margin={logoImage ? 0 : 4}      // SEM margem quando há logo
                  components={componentsOpts}
                />
              </div>
            </div>
          </div>
          <div className="info-box">
            <p><strong>💡 Dica:</strong> Clique com botão direito na imagem acima e selecione "Salvar imagem como..." para fazer download do seu QR Code!</p>
          </div>
        </div>
      </div>

      <footer className="footer">
        <p>Desenvolvido com ❤️ usando React e <a href="https://github.com/SumiMakito/Awesome-qr.js" target="_blank" rel="noopener noreferrer">Awesome QR</a> por Makito</p>
        <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: 0.8 }}>Template original: CodeSandbox</p>
      </footer>
    </div>
  );
}
